import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { MyProfile, ProfileField, Visibility } from '../../shared/api/types';
import { Alert, Button, Card, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';

const FIELDS: { key: ProfileField; label: string }[] = [
  { key: 'bio', label: 'Acerca de mí' },
  { key: 'phone', label: 'Celular y teléfono' },
  { key: 'email', label: 'Correo' },
  { key: 'birthday', label: 'Cumpleaños (día y mes)' },
  { key: 'address', label: 'Dirección' },
  { key: 'ministries', label: 'Mis ministerios' },
  { key: 'family', label: 'Mi familia' },
];
const LEVELS: { value: Visibility; label: string }[] = [
  { value: 'church', label: 'Toda mi iglesia' },
  { value: 'leaders', label: 'Solo líderes y pastor' },
  { value: 'private', label: 'Solo yo' },
];

/** Mi perfil público: lo escribo yo y decido quién ve cada dato. */
export function MyPublicProfileCard({ personId }: { personId: string }) {
  const profile = useAsync((signal) => api.get<MyProfile>('/identity/me/profile', signal), [personId]);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const visibility = Object.fromEntries(FIELDS.map((f) => [f.key, String(form.get(`vis-${f.key}`))])) as Record<ProfileField, Visibility>;
    setBusy(true);
    setFeedback(null);
    try {
      await api.put('/identity/me/profile', { bio: String(form.get('bio') ?? '').trim(), visibility });
      setFeedback({ tone: 'success', text: 'Tu perfil quedó guardado.' });
      profile.reload();
    } catch (e) {
      setFeedback({ tone: 'error', text: errorMessage(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Mi perfil público" action={<Link to={`/app/personas/${personId}`}>Ver cómo me ven</Link>}>
      {profile.status === 'loading' ? <Loading /> : null}
      {profile.status === 'error' ? <ErrorState error={profile.error} onRetry={profile.reload} /> : null}
      {profile.status === 'ready' ? (
        <form className="form" onSubmit={save} noValidate>
          {feedback ? <Alert tone={feedback.tone}>{feedback.text}</Alert> : null}
          <div className="field">
            <label htmlFor="profile-bio">Acerca de mí</label>
            <textarea id="profile-bio" name="bio" rows={3} maxLength={1000} placeholder="Cuéntale a tu iglesia un poco de ti." defaultValue={profile.data.bio ?? ''} />
          </div>
          <p className="small muted">Tu celular y teléfono se capturan en «Información», arriba; aquí decides quién los ve.</p>
          <fieldset className="visibility">
            <legend>¿Quién ve cada dato?</legend>
            {FIELDS.map((f) => (
              <div key={f.key} className="visibility__row">
                <label htmlFor={`vis-${f.key}`}>{f.label}</label>
                <select id={`vis-${f.key}`} name={`vis-${f.key}`} defaultValue={profile.data.visibility[f.key]}>
                  {LEVELS.map((l) => (
                    <option key={l.value} value={l.value}>
                      {l.label}
                    </option>
                  ))}
                </select>
              </div>
            ))}
            <p className="small muted">Tu nombre siempre es visible para tu iglesia. El pastor y la administración también ven tu expediente.</p>
          </fieldset>
          <div className="actions">
            <Button type="submit" busy={busy}>
              Guardar mi perfil
            </Button>
          </div>
        </form>
      ) : null}
    </Card>
  );
}
